import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { kbApi, portalApi } from '../services/supportApi';
import type {
  KBArticleCreate,
  KBArticleUpdate,
  ListArticlesParams,
  PortalListArticlesParams,
} from '../types';

export const kbKeys = {
  all: ['kb'] as const,
  categories: (parentId?: string) => [...kbKeys.all, 'categories', parentId] as const,
  category: (id: string) => [...kbKeys.all, 'category', id] as const,
  articles: (params?: ListArticlesParams) => [...kbKeys.all, 'articles', params] as const,
  article: (id: string) => [...kbKeys.all, 'article', id] as const,
};

function cleanParams(params?: ListArticlesParams): ListArticlesParams | undefined {
  if (!params) return undefined;
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined)
  ) as ListArticlesParams;
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

function cleanPortalParams(
  params?: PortalListArticlesParams
): PortalListArticlesParams | undefined {
  if (!params) return undefined;
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined)
  ) as PortalListArticlesParams;
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

// Categories
export function useKBCategories(params?: { parent_id?: string; public_only?: boolean }) {
  return useQuery({
    queryKey: kbKeys.categories(params?.parent_id),
    queryFn: () => kbApi.listCategories(params?.parent_id, params?.public_only),
  });
}

export function useKBCategory(id: string) {
  return useQuery({
    queryKey: kbKeys.category(id),
    queryFn: () => kbApi.getCategory(id),
    enabled: !!id,
  });
}

export function useCreateKBCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
      slug: string;
      description?: string;
      parent_id?: string;
      position?: number;
      icon?: string;
      product_ids?: string[];
      is_public?: boolean;
    }) => kbApi.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbKeys.categories() });
    },
  });
}

export function useUpdateKBCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<{
      name: string;
      slug: string;
      description: string;
      position: number;
      icon: string;
      product_ids: string[];
      is_public: boolean;
    }> }) => kbApi.updateCategory(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: kbKeys.category(id) });
      queryClient.invalidateQueries({ queryKey: kbKeys.categories() });
    },
  });
}

export function useDeleteKBCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => kbApi.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbKeys.categories() });
    },
  });
}

// Articles
export function useKBArticles(params?: ListArticlesParams) {
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only recompute when specific fields change
  const stableParams = useMemo(() => cleanParams(params), [
    params?.category_id,
    params?.status,
    params?.is_internal,
    params?.search,
    params?.tags,
    params?.tags_mode,
    params?.offset,
    params?.limit,
  ]);

  return useQuery({
    queryKey: kbKeys.articles(stableParams),
    queryFn: () => kbApi.listArticles(stableParams),
  });
}

export function useKBArticle(id: string) {
  return useQuery({
    queryKey: kbKeys.article(id),
    queryFn: () => kbApi.getArticle(id),
    enabled: !!id,
  });
}

export function useCreateKBArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: KBArticleCreate) => kbApi.createArticle(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbKeys.articles() });
      queryClient.invalidateQueries({ queryKey: kbKeys.categories() });
    },
  });
}

export function useUpdateKBArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: KBArticleUpdate }) => kbApi.updateArticle(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: kbKeys.article(id) });
      queryClient.invalidateQueries({ queryKey: kbKeys.articles() });
    },
  });
}

export function usePublishKBArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => kbApi.publishArticle(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: kbKeys.article(id) });
      queryClient.invalidateQueries({ queryKey: kbKeys.articles() });
    },
  });
}

export function useArchiveKBArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => kbApi.archiveArticle(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: kbKeys.article(id) });
      queryClient.invalidateQueries({ queryKey: kbKeys.articles() });
    },
  });
}

export function useDeleteKBArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => kbApi.deleteArticle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbKeys.articles() });
      queryClient.invalidateQueries({ queryKey: kbKeys.categories() });
    },
  });
}

// `useRecordArticleView` was deleted with `kbApi.recordView`: no surface called it,
// and `GET /kb/articles/{id}` already records the view server-side.

export function useRecordArticleFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, helpful }: { id: string; helpful: boolean }) => kbApi.recordFeedback(id, helpful),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: kbKeys.article(id) });
    },
  });
}

// --- Portal (external reader) hooks ---
//
// The portal twins of the four read/feedback hooks above. They exist as their own
// hooks rather than a flag on the internal ones because they are a different
// SURFACE, not a different filter: a portal reader holds an external token only,
// so their calls must go through `portalApi`/`externalAxiosInstance` against
// /api/v1/external/support/* — the internal `kbApi` sits behind `require_auth` and
// 401s for them. Their own key namespace keeps the two caches apart, since the
// portal routes return only published, non-internal rows.

export const portalKbKeys = {
  all: ['portal-kb'] as const,
  categories: (parentId?: string) => [...portalKbKeys.all, 'categories', parentId] as const,
  articles: (params?: PortalListArticlesParams) => [...portalKbKeys.all, 'articles', params] as const,
  article: (id: string) => [...portalKbKeys.all, 'article', id] as const,
};

export function usePortalKBCategories(parentId?: string) {
  return useQuery({
    queryKey: portalKbKeys.categories(parentId),
    queryFn: () => portalApi.listKBCategories(parentId),
  });
}

export function usePortalKBArticles(params?: PortalListArticlesParams) {
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only recompute when specific fields change
  const stableParams = useMemo(() => cleanPortalParams(params), [
    params?.category_id,
    params?.search,
    params?.offset,
    params?.limit,
  ]);

  return useQuery({
    queryKey: portalKbKeys.articles(stableParams),
    queryFn: () => portalApi.listKBArticles(stableParams),
  });
}

export function usePortalKBArticle(id: string) {
  return useQuery({
    queryKey: portalKbKeys.article(id),
    queryFn: () => portalApi.getKBArticle(id),
    enabled: !!id,
  });
}

export function useRecordPortalArticleFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, helpful }: { id: string; helpful: boolean }) =>
      portalApi.recordKBArticleFeedback(id, helpful),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: portalKbKeys.article(id) });
    },
  });
}
